<template>
    <aside class="sidebar" :class="{ 'sidebar-collapsed': isCollapsed, 'sidebar-sticky': isSticky }" data-sidebar>
        <!-- Toggle button for mobile/responsive design -->
        <button @click="toggleSidebar" class="sidebar-toggle" :aria-label="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'" :title="isCollapsed ? 'Expand' : 'Collapse'">
            <ion-icon :name="isCollapsed ? 'chevron-forward-outline' : 'chevron-back-outline'"></ion-icon>
        </button>
        
        <!-- Header section with WJ logo - completely hidden when collapsed -->
        <transition name="fade-slide">
            <div class="sidebar-info" v-if="!isCollapsed">
                <div class="contact-header">
                    <div class="contact-icon wj-icon">
                        <span class="wj-text">WJ</span>
                    </div>
                </div>
            </div>
        </transition>
        <!-- Nothing shown when collapsed - icons in the list below are sufficient -->
        
        <div class="separator"></div>
        
        <!-- Contact list section - always visible -->
        <ul class="contacts-list">
            <!-- Email contact -->
            <li class="contact-item">
                <div class="icon-box" :title="'Email: ' + profileData.email">
                    <ion-icon name="mail-outline"></ion-icon>
                </div>
                <transition name="fade-slide">
                    <div class="contact-info" v-if="!isCollapsed">
                        <p class="contact-title">Email</p>
                        <a class="contact-link" :href="'mailto:' + profileData.email" :title="profileData.email">
                            {{ profileData.email }}
                        </a>
                    </div>
                </transition>
            </li>

            <!-- LinkedIn contact -->
            <li class="contact-item">
                <div class="icon-box" title="LinkedIn">
                    <ion-icon name="logo-linkedin"></ion-icon>
                </div>
                <transition name="fade-slide">
                    <div class="contact-info" v-if="!isCollapsed">
                        <p class="contact-title">LinkedIn</p>
                        <a class="contact-link" :href="profileData.linkedin" :title="profileData.linkedin"
                            target="_blank" rel="noopener noreferrer">
                            {{ profileData.linkedin }}
                        </a>
                    </div>
                </transition>
            </li>

            <!-- GitHub contact -->
            <li class="contact-item">
                <div class="icon-box" title="GitHub">
                    <ion-icon name="logo-github"></ion-icon>
                </div>
                <transition name="fade-slide">
                    <div class="contact-info" v-if="!isCollapsed">
                        <p class="contact-title">Github</p>
                        <a class="contact-link" :href="profileData.github" :title="profileData.github"
                            target="_blank" rel="noopener noreferrer">
                            {{ profileData.github }}
                        </a>
                    </div>
                </transition>
            </li>

            <!-- Additional information -->
            <li class="contact-item location-item">
                <div class="icon-box" title="Location: Johor, Malaysia">
                    <ion-icon name="location-outline"></ion-icon>
                </div>
                <transition name="fade-slide">
                    <div class="contact-info" v-if="!isCollapsed">
                        <p class="contact-title">Location</p>
                        <p class="contact-link">{{ profileData.location || 'Johor, Malaysia' }}</p>
                    </div>
                </transition>
            </li>

            <!-- Page version info -->
            <li class="contact-item version-item">
                <div class="icon-box" :title="'Version: ' + version">
                    <ion-icon name="git-branch-outline"></ion-icon>
                </div>
                <transition name="fade-slide">
                    <div class="contact-info" v-if="!isCollapsed">
                        <p class="contact-title">Page Version</p>
                        <p class="contact-link" :title="version">{{ version }}</p>
                    </div>
                </transition>
            </li>

            <!-- Copyright info - enhanced design with mobile optimization -->
            <li class="contact-item copyright-item">
                <div class="icon-box" :title="'Copyright © ' + new Date().getFullYear()">
                    <ion-icon name="shield-checkmark-outline"></ion-icon>
                </div>
                <transition name="fade-slide">
                    <div class="contact-info copyright-info" v-if="!isCollapsed">
                        <p class="contact-title">Copyright Protected</p>
                        <div class="copyright-content">
                            <p class="copyright-text">
                                <span class="copyright-symbol">&copy;</span>
                                <span class="copyright-year">{{ new Date().getFullYear() }}</span>
                            </p>
                            <p class="copyright-holder">All Rights Reserved</p>
                        </div>
                    </div>
                </transition>
            </li>

            <!-- Technology info -->
            <li class="contact-item tech-item">
                <div class="icon-box" title="Built with Vue.js">
                    <ion-icon name="code-outline"></ion-icon>
                </div>
                <transition name="fade-slide">
                    <div class="contact-info" v-if="!isCollapsed">
                        <p class="contact-title">Crafted with</p>
                        <p class="contact-link">
                            <span class="tech-highlight">Vue.Js</span>
                        </p>
                    </div>
                </transition>
            </li>
        </ul>
        
        <div class="separator"></div>
        
        <!-- Download CV button -->
        <transition name="fade-slide">
            <div class="cv-download-container" v-if="!isCollapsed">
                <a href="#" class="cv-download-btn" title="Download my CV">
                    <ion-icon name="download-outline"></ion-icon>
                    <span>Download CV</span>
                </a>
                
                <!-- Chat button - visible when sidebar expanded -->
                <!-- <button @click="openChat" class="chat-btn" title="Start a chat with me">
                    <ion-icon name="chatbubbles-outline"></ion-icon>
                    <span>Chat with Me</span>
                </button> -->
            </div>
        </transition>
        
        <!-- Minimized buttons when collapsed -->
        <transition name="fade-scale">
            <div class="cv-download-container" v-if="isCollapsed">
                <a href="#" class="cv-download-btn cv-download-btn-mini" title="Download my CV">
                    <ion-icon name="download-outline"></ion-icon>
                </a>
                
                <!-- Minimized chat button -->
                <!-- <button @click="openChat" class="chat-btn chat-btn-mini" title="Start a chat with me">
                    <ion-icon name="chatbubbles-outline"></ion-icon>
                </button> -->
            </div>
        </transition>
    </aside>
</template>

<script>
import pkg from '../../../package.json';
import { getImagePathWJ } from '@/utils/images/imageLoader.js';
import { openTawkToChat } from '@/utils/services/tawkChat.js';
import { 
    optimizeSidebarTransition,
    staggeredAnimation,
    fadeIn
} from '@/utils/animation/mobileAnimation.js';

export default {
    data() {
        return {
            version: pkg.version,
            isCollapsed: false,
            isSticky: true,
            lastScrollPosition: 0,
            hasResized: false // Track if we've already handled initial resize
        };
    },
    props: {
        profileData: Object,
        // We keep these props for compatibility with MainView.vue
        // but we don't use them anymore since contacts are always visible
        showContacts: Boolean,
        toggleContacts: Function
    },
    mounted() {
        // Check if user has a preference for collapsed sidebar
        const savedState = localStorage.getItem('sidebarCollapsed');
        if (savedState) {
            this.isCollapsed = JSON.parse(savedState);
        } else {
            // Default to collapsed on mobile
            this.isCollapsed = window.innerWidth < 768;
        }
        
        // Always ensure sticky sidebar is enabled
        this.isSticky = true;
        
        // Force the sidebar to be visible after a slight delay
        setTimeout(() => {
            // Set additional attributes to ensure visibility
            const sidebarElement = document.querySelector('.sidebar');
            if (sidebarElement) {
                sidebarElement.style.display = 'block';
                sidebarElement.style.visibility = 'visible';
                
                // Apply performance optimizations to the sidebar
                optimizeSidebarTransition(sidebarElement);
                
                // Apply staggered animation to contact items
                const contactItems = document.querySelectorAll('.contact-item');
                staggeredAnimation(contactItems, fadeIn, {
                    staggerDelay: 70,
                    initialDelay: 100,
                    duration: 400
                });
            }
        }, 100);
        
        // Add scroll event listener for sticky behavior
        window.addEventListener('scroll', this.handleScroll);
        
        // Add resize listener to adjust for screen size changes
        window.addEventListener('resize', this.handleResize);
        
        // Initial check
        this.handleResize();
        
        // Emit initial state to parent
        this.$emit('sidebar-state', this.isCollapsed);
        
        // Dispatch initial state for layout handler
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('sidebarStateChange', {
                detail: { isCollapsed: this.isCollapsed }
            }));
            
            // Set initial body class for CSS targeting
            if (this.isCollapsed) {
                document.body.classList.add('sidebar-is-collapsed');
                document.body.classList.remove('sidebar-is-expanded');
            } else {
                document.body.classList.add('sidebar-is-expanded');
                document.body.classList.remove('sidebar-is-collapsed');
            }
        }, 100);
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        getImagePathWJ,
        openChat() {
            // Use the Tawk.to chat utility to open the chat widget
            openTawkToChat();
        },
        toggleSidebar() {
            // Apply performance optimizations before toggling
            const sidebarElement = document.querySelector('.sidebar');
            if (sidebarElement) {
                optimizeSidebarTransition(sidebarElement);
            }

            this.isCollapsed = !this.isCollapsed;
            // Save preference
            localStorage.setItem('sidebarCollapsed', JSON.stringify(this.isCollapsed));
            // Emit state change to parent
            this.$emit('sidebar-state', this.isCollapsed);
            
            // Update body class immediately for faster CSS response
            if (this.isCollapsed) {
                document.body.classList.add('sidebar-is-collapsed');
                document.body.classList.remove('sidebar-is-expanded');
            } else {
                document.body.classList.add('sidebar-is-expanded');
                document.body.classList.remove('sidebar-is-collapsed');
            }
            
            // Dispatch event after a small delay to avoid layout thrashing
            requestAnimationFrame(() => {
                window.dispatchEvent(new CustomEvent('sidebarStateChange', {
                    detail: { isCollapsed: this.isCollapsed }
                }));
            });
        },
        handleScroll() {
            const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
            
            // Always keep sidebar sticky for better UX regardless of scroll position
            this.isSticky = true;
            
            // Keep track of scroll position but don't auto-hide the sidebar anymore when scrolling
            this.lastScrollPosition = currentScrollPosition;
        },
        handleResize() {
            // Only collapse on initial page load for small screens
            if (window.innerWidth < 768 && !this.hasResized) {
                this.isCollapsed = true;
                // Emit change to parent
                this.$emit('sidebar-state', this.isCollapsed);
                this.hasResized = true; // Mark that we've already done the initial resize
            }
        }
    }
}
</script>

<style scoped>
/* Modern sidebar styles */
.sidebar {
  position: fixed; /* Always fixed instead of relative */
  left: 20px; /* Adjusted position from the left edge */
  top: 15px; /* Position from the top edge */
  background: var(--eerie-black-2);
  border: 1px solid var(--jet);
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  z-index: 10; /* Lower z-index so it doesn't overlap important content */
  transform: translateZ(0); /* Force hardware acceleration */
  backface-visibility: hidden; /* Prevent flickering on some browsers */
  perspective: 1000; /* Another hardware acceleration property */
  transition: width 0.3s cubic-bezier(0.25, 0.1, 0.25, 1), 
              transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1); /* Use bezier for smoother animation */
  width: 300px;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  display: block !important; /* Always display */
  visibility: visible !important; /* Always visible */
}

/* Sticky sidebar */
.sidebar-sticky {
  position: fixed; /* Change from sticky to fixed for consistent behavior */
  top: 20px;
  left: 20px; /* Fixed position needs left value */
  height: fit-content;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  scroll-behavior: smooth;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  align-self: flex-start; /* Ensures sticky positioning works properly */
  will-change: transform, width; /* Optimize browser rendering by specifying which properties will change */
  transform: translateZ(0); /* Force GPU acceleration for smoother scrolling */
  backface-visibility: hidden; /* Prevent flickering */
  transition: width 0.3s cubic-bezier(0.25, 0.1, 0.25, 1), 
              transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1); /* Smoother easing */
  opacity: 1 !important; /* Always fully visible when sticky */
  z-index: 5; /* Lower z-index to avoid overlapping content */
}

/* Hide scrollbar completely for a cleaner look */
.sidebar-sticky::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
  width: 0;
  background: transparent;
}

/* Collapsed sidebar */
.sidebar-collapsed {
  width: 80px;
  padding: 20px 10px;
  transform: translateZ(0); /* Ensure hardware acceleration is maintained */
  margin-right: 0 !important;
  will-change: width; /* Hint to browser about what will change */
  transition: width 0.3s cubic-bezier(0.25, 0.1, 0.25, 1); /* Smoother width transition */
}

.sidebar:hover {
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.4);
  transform: translateY(-3px);
}

/* Toggle button for sidebar */
.sidebar-toggle {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--jet);
  border: none;
  color: var(--orange-yellow-crayola);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 11;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.sidebar-toggle:hover {
  background: var(--onyx);
  transform: scale(1.1);
}

/* Contact header styling */
.contact-header {
  display: flex;
  align-items: center;
  justify-content: center; /* Center the logo */
  margin-bottom: 15px;
}

.contact-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--onyx), var(--eerie-black-1));
  border-radius: 50%;
  color: var(--orange-yellow-crayola);
  font-size: 22px;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
}

/* WJ Icon styling */
@keyframes gradient-shift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes subtle-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.wj-icon {
  background: linear-gradient(135deg, #3498db, #9b59b6, #2ecc71);
  background-size: 200% 200%;
  animation: gradient-shift 5s ease infinite, subtle-pulse 3s ease-in-out infinite;
  box-shadow: 0 5px 15px rgba(52, 152, 219, 0.3);
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  width: 60px; /* Increase size */
  height: 60px; /* Increase size */
}

/* Copyright styling - enhanced */
.copyright-item::before {
  background: #e74c3c;
}

.copyright-item .icon-box {
  color: #e74c3c;
}

.copyright-info {
  display: flex;
  flex-direction: column;
}

.copyright-text {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--white-2);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
}

.copyright-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.copyright-holder {
  font-size: 0.75rem;
  color: var(--light-gray);
  opacity: 0.9;
  font-style: italic;
  margin: 0;
  padding-left: 2px;
}

.copyright-year {
  font-weight: 500;
  color: var(--white-2);
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

@media (max-width: 768px) {
  .copyright-symbol {
    animation: pulse 2s infinite ease-in-out;
  }
  
  /* When sidebar is collapsed on mobile, adjust the copyright icon */
  .sidebar-collapsed .copyright-item .icon-box {
    background: rgba(231, 76, 60, 0.1);
  }
}

/* Making copyright icon more visible in collapsed mode */
.sidebar-collapsed .copyright-item .icon-box {
  transform: scale(1.05);
  box-shadow: 0 3px 8px rgba(231, 76, 60, 0.2);
}

.sidebar-collapsed .copyright-item .icon-box:hover {
  transform: scale(1.15) rotateY(180deg);
}

.contact-info {
  width: calc(100% - 50px);
}

.contact-title {
  font-size: 0.8rem;
  color: var(--light-gray);
  text-transform: uppercase;
  font-weight: 500;
  margin-bottom: 3px;
}

.contact-link {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--white-2);
  font-size: 0.95rem;
  transition: color 0.2s ease;
}

.contact-link:hover {
  color: var(--orange-yellow-crayola);
}

/* Separator styling */
.separator {
  width: 100%;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--jet), transparent);
  margin: 15px 0;
}

/* Tech highlight styling */
.tech-highlight {
  background: linear-gradient(to right, #41B883, #34495E);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 600;
}

/* Download CV button */
.cv-download-container {
  display: flex;
  justify-content: center;
  margin-top: 5px;
}

.cv-download-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background: linear-gradient(to right, var(--orange-yellow-crayola), #ffa41b);
  border-radius: 30px;
  color: var(--eerie-black-1);
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 5px 15px rgba(255, 215, 108, 0.2);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); /* Bouncy effect */
  will-change: transform, box-shadow;
  transform: translateZ(0); /* Hardware acceleration */
}

.cv-download-btn:hover {
  transform: translateY(-3px) translateZ(0);
  box-shadow: 0 8px 20px rgba(255, 215, 108, 0.3);
}

.cv-download-btn:active {
  transform: translateY(0) translateZ(0);
  box-shadow: 0 3px 10px rgba(255, 215, 108, 0.2);
}

/* Mini button styles */
.cv-download-btn-mini {
  padding: 10px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Chat button styles */
.chat-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 50px;
    padding: 10px 20px;
    margin-top: 10px;
    width: 100%;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.3s ease;
}

.chat-btn ion-icon {
    font-size: 1.2rem;
}

.chat-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(102, 126, 234, 0.4);
}

.chat-btn-mini {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    padding: 0;
}

/* Responsive styles for chat button */
@media (max-width: 767px) {
    .chat-btn:not(.chat-btn-mini) {
        padding: 8px 15px;
        font-size: 0.75rem;
    }
    
    .chat-btn ion-icon {
        font-size: 1rem;
    }
}

@media (max-width: 1100px) {
  .sidebar {
    width: 260px;
  }
  
  .sidebar-collapsed {
    width: 80px;
  }
}

@media (max-width: 991px) {
  .sidebar-sticky {
    top: 10px;
    max-height: calc(100vh - 20px);
  }
}

@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    top: 10px;
    left: 15px; /* Adjusted left position to avoid overlap */
    width: 280px; /* Slightly smaller on mobile */
    transform: translateX(0);
    transition: transform 0.3s ease, width 0.3s ease, opacity 0.3s ease;
    opacity: 0.95;
    z-index: 100;
  }
  
  .sidebar:hover {
    opacity: 1;
    transform: translateX(0) translateY(0) !important;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.5);
  }
  
  .sidebar-collapsed {
    width: 60px;
    transform: translateX(-5px); /* Reduced negative transform */
    opacity: 0.9; /* More visible when collapsed */
  }
  
  /* Expanded sidebar takes more room on mobile */
  .sidebar:not(.sidebar-collapsed) {
    height: auto;
    max-height: calc(100vh - 20px);
    overflow-y: auto;
  }
  
  /* Force fixed position on mobile to prevent sidebar from disappearing */
  .sidebar-sticky {
    position: fixed !important;
    top: 10px !important;
    display: block !important;
    visibility: visible !important;
    height: auto !important;
    max-height: calc(100vh - 20px) !important;
    will-change: transform !important;
  }
  
  /* Improved copyright display for mobile */
  .copyright-item {
    background: linear-gradient(to right, rgba(231, 76, 60, 0.05), transparent);
  }
  
  .copyright-text {
    flex-wrap: wrap;
  }
  
  .copyright-symbol, .copyright-year {
    font-size: 0.9rem;
  }
  
  .copyright-holder {
    width: 100%;
    margin-top: 2px;
    text-align: left;
  }
}

@media (max-width: 580px) {
  .contacts-list {
    width: 100%;
  }
  
  .contact-item {
    flex-wrap: nowrap;
  }

  /* Enhanced mobile styles */
  .sidebar {
    border-radius: 12px;
    padding: 15px 10px;
    margin-bottom: 15px;
  }

  .contact-heading {
    font-size: 1.1rem;
  }

  .contact-description p {
    font-size: 0.8rem;
  }
  
  /* Collapse by default on small screens */
  .sidebar-collapsed {
    width: 50px;
    padding: 10px 5px;
    transform: translateX(-5px);
  }
  
  /* Make toggle button smaller */
  .sidebar-toggle {
    width: 24px;
    height: 24px;
    top: 10px;
    right: 10px;
  }
  
  .icon-box {
    width: 32px;
    height: 32px;
  }
  
  /* Further optimized copyright for smaller screens */
  .copyright-text {
    font-size: 0.85rem;
  }
  
  .copyright-symbol {
    font-size: 0.95rem;
  }
}

/* Chat button styles */
.chat-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 50px;
    padding: 10px 20px;
    margin-top: 10px;
    width: 100%;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.3s ease;
}

.chat-btn ion-icon {
    font-size: 1.2rem;
}

.chat-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(102, 126, 234, 0.4);
}

.chat-btn-mini {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    padding: 0;
}

/* Transition group for sidebar state changes */
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-slide-enter, .fade-slide-leave-to /* .fade-slide-leave-active in <2.1.8 */ {
  opacity: 0;
  transform: translateY(-10px);
}

.wj-text {
  color: white;
  font-weight: 700;
  font-size: 22px; /* Larger text */
  letter-spacing: 0.5px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  position: relative;
  z-index: 2;
}
</style>
